import { useEffect, useState } from 'react';
import { Duration } from 'luxon';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useUpdateSharedDocument } from '@utils/api/magic-links/use-update-shared-document.tsx';

type Props = {
  setTimeCodeOpen: (value: boolean) => void;
};

const SharedTranscriptTimecode = ({ setTimeCodeOpen }: Props) => {
  const { setTime, setTimecodeOffset, timecodeOffset } = useAudio();
  const { t } = useTranslation();
  const { documentResult, session } = useSharedTranscript();
  const { mutate: updateDocument, isLoading: saveDocumentLoading } =
    useUpdateSharedDocument();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('0');
  const [seconds, setSeconds] = useState('0');
  const [milliseconds, setMilliseconds] = useState('0');

  useEffect(() => {
    if (timecodeOffset === undefined) return;

    const duration = Duration.fromObject({ seconds: parseInt(timecodeOffset) });
    const { hours, minutes, seconds, milliseconds } = duration
      .shiftTo('hours', 'minutes', 'seconds', 'milliseconds')
      .toObject();

    if (hours) setHours(hours.toString().padStart(1, '0'));
    if (minutes) setMinutes(minutes.toString().padStart(1, '0'));
    if (seconds) setSeconds(seconds.toString().padStart(1, '0'));
    if (milliseconds) setMilliseconds(milliseconds.toString().padStart(1, '0'));
  }, [timecodeOffset]);

  const handleSaveClick = () => {
    const isValidNumber = (value: string): boolean => !isNaN(parseInt(value));

    if (
      !isValidNumber(hours) ||
      !isValidNumber(minutes) ||
      !isValidNumber(seconds) ||
      !isValidNumber(milliseconds)
    ) {
      return;
    }

    if (!documentResult?.result || !session) {
      return;
    }

    toast(t('setting-timecode-offset'));

    const totalTimeInSeconds =
      parseInt(hours) * 3600 +
      parseInt(minutes) * 60 +
      parseInt(seconds) +
      parseInt(milliseconds) / 1000;

    const offsetStr = totalTimeInSeconds.toString();
    setTimecodeOffset(offsetStr);
    setTime(totalTimeInSeconds);

    updateDocument(
      {
        documentId: documentResult?.result?.id,
        sessionId: session?.session_id,
        timecode_offset: offsetStr
      },
      {
        onSuccess: () => {
          toast.success(t('timecode-offset-updated'));
          handleCloseModal();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const fallback = t('error-updating-timecode-offset');
          const data = error?.response?.data;
          const message =
            data?.error_description ||
            data?.detail ||
            data?.error ||
            data?.message ||
            fallback;

          toast.error(message);
        }
      }
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeCodeOpen(false);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      size="small"
      title={t('set-timecode')}
      caption={t(
        'enter-the-starting-timecode-that-you-want-to-set-for-your-file-(ms)'
      )}
      footerActions={[
        <Button
          onClick={handleCloseModal}
          label={t('cancel')}
          size={'small'}
          variant={'smoke'}
          disabled={saveDocumentLoading}
        />,
        <Button
          onClick={handleSaveClick}
          label={t('save')}
          size={'small'}
          variant={'primary'}
          loading={saveDocumentLoading}
          disabled={saveDocumentLoading}
        />
      ]}
    >
      <div className="grid grid-cols-4 gap-2">
        <Input
          id="hours"
          size="small"
          value={hours}
          onChange={event => setHours(event.target.value)}
          placeholder="H"
          numeric
          className={'text-center'}
          label={t('hours')}
        />
        <Input
          id="minutes"
          size="small"
          value={minutes}
          onChange={event => setMinutes(event.target.value)}
          placeholder="M"
          numeric
          className={'text-center'}
          label={t('minutes')}
        />
        <Input
          id="seconds"
          value={seconds}
          size="small"
          onChange={event => setSeconds(event.target.value)}
          placeholder="S"
          numeric
          className={'text-center'}
          label={t('seconds')}
        />
        <Input
          id="milliseconds"
          size="small"
          value={milliseconds}
          onChange={event => setMilliseconds(event.target.value)}
          placeholder="Ms"
          numeric
          className={'text-center'}
          label={t('ms')}
        />
      </div>
    </Modal>
  );
};

export default SharedTranscriptTimecode;
